import React, { useEffect, useState } from "react";
import {merge, mergeWith, isArray, cloneDeep, pick} from "lodash";
import PropTypes from "prop-types";
import Footer from "common/Footer";
import StyledButton from "common/StyledButton";
import StickyFooterLayout from "common/StickyFooterLayout";
import StudentSurveyPropertiesStep from "./StudentSurveyPropertiesStep";
import StudentSurveyBuildStep from "./StudentSurveyBuildStep";
import StudentSurveyTabs from "./StudentSurveyTabs";
import css from "./css/StudentSurveyTemplate.scss";
import StudentSurveyAudienceStep from "./StudentSurveyAudienceStep";
import StudentSurveyTimingStep from "./StudentSurveyTimingStep";
import StudentSurveyConfirmSetUp from "./StudentSurveyConfirmSetUp";
import { getWord } from "utils/tenant";
import { isEmptyString } from "utils/text";
import useStarfishAPI from "hooks/useStarfishAPI";
import moment from "moment";
import request from "utils/request";
import ConfirmDialog from "common/ConfirmDialog";
import {formattedDateTime, formHasError} from "./templateValidation";
import Toast from "common/Toast";
import ErrorMessage from "common/ErrorMessage";

const navigationTabs = [{
        id: 'properties',
        name: 'Properties'
    }, {
        id: 'build',
        name: 'Build'
    }, {
        id: 'audience',
        name: 'Audience'
    }, {
        id: 'timing',
        name: 'Timing'
    }, {
        id: 'confirmSchedule',
        name: 'Confirm & Schedule'
    }];

function StudentSurveyTemplate({cancel, displayToast, surveyTemplateId = null, isCopy = false}) {
    const emptyState = {
        id: null,
        title: "",
        description: null,
        permissions: [],
        elements: [],
        timing: {
            openDate: null,
            closeDate: null,
            openTime: null,
            closeTime: null
        },
        audience: null,
        enabled: false,
        validationPassed: null
    };

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [selectedTab, selectTab] = useState(0);
    const [formData, setFormData] = useState(emptyState);
    const [saveSurveyFormResponse, saveSurveyFormAPI] = useStarfishAPI({isNewAPI: true})
    const [surveyFormResponse, getSurveyFormAPI] = useStarfishAPI({isNewAPI: true})
    const [cohortTemplatesResponse, getCohortTemplatesAPI] = useStarfishAPI()
    const [rolesResponse, getRolesAPI] = useStarfishAPI()
    const [error, setError] = useState(false);

    const next = () => {
        if(selectedTab !== navigationTabs.length - 1) selectTab(selectedTab + 1)
    }

    const lastTab = () => selectedTab === (navigationTabs.length - 1)

    // If an object is an instance of moment, format it as a date string.
    // If not, assume the format is correct already.
    const convertMomentToDateString = (momentObject) =>
        momentObject instanceof moment ? momentObject?.format("YYYY-MM-DD") : momentObject;

    // Transforms the UI state data into a format that the backend expects
    const studentSurveyTemplate = (uiData, schedule) => {
        const appFormData = cloneDeep(uiData)

        const appTiming = merge(appFormData.timing, {
            openDate: convertMomentToDateString(appFormData.timing?.openDate),
            closeDate: convertMomentToDateString(appFormData.timing?.closeDate)
        });

        // Removing id since it is only needed for UI
        const appElements = appFormData.elements.map(({id, ...rest}) => rest);// eslint-disable-line no-unused-vars
        const appPermissions = appFormData.permissions?.filter(permission => permission.access !== 'none')

        return {
            ...appFormData,
            timing: appTiming,
            permissions: appPermissions,
            elements: appElements,
            enabled: schedule ? true : appFormData.enabled,
            validationPassed: schedule ? true : appFormData.validationPassed
        }
    }

    const onScheduleClick = () =>
        formHasError(formData) ? setShowToast(true) : setIsDialogOpen(true)

    const onSave = (nextAction, schedule = false) => () => {
        // Don't allow removing the open date once survey is validated
        if (formData.validationPassed && !formData.timing.openDate) {
            setShowToast(true);
            return;
        }
        saveSurveyFormAPI('../api/student-survey-template', {
            method: 'POST',
            headers: {
                'Content-Type': "application/json"
            },
            body: JSON.stringify(studentSurveyTemplate(formData, schedule))
        }).then( response => {
                if (response.success) {
                    setFormData((oldFormData) => merge({}, oldFormData, {id: response.data.id}))
                    nextAction(response.data.id)
                    if (nextAction === cancel) {
                        displayToast(`${getWord('studentSurvey', false)} ${schedule && !!response.data.status ? `is in ${response.data.status} status.` : 'saved.'}`)
                    }
                }
            }
        )
    }

    // Not all fields get copied in duplicate template. Pick the fields that do get copied in the template.
    const duplicateTemplateDataFields = (copiedData) => {
        return merge({}, pick(copiedData, ['title', 'description', 'timezone', 'permissions', 'elements', 'audience']),
            {
                title: "Copy of " + copiedData.title,
                timing: {
                    openDate: null,
                    closeDate: null,
                    openTime: null,
                    closeTime: null
                }
            })
    }
    useEffect(() => {
        if (surveyTemplateId !== null) {
            getSurveyFormAPI(`../api/student-survey-template/${surveyTemplateId}/`, { method: 'GET' })
                .then( response => {
                    if (response.success) {
                       // This is making a duplicate template
                        if (isCopy) {
                            setFormData(duplicateTemplateDataFields(response.data))
                            selectTab(0)
                          } else {
                            setFormData(response.data)
                            selectTab(navigationTabs.length - 1)
                        }
                    }
                })
        } else {
            getSurveyFormAPI('../api/student-survey-template/template-defaults', { method: 'GET' });
        }
        getCohortTemplatesAPI('getCohortTemplates.html',
            {   method: 'POST',
                body: request.urlParams({
                    start: 0,
                    limit: -1
                })});

        getRolesAPI('../instructor/getRoles.html', { method: 'GET' });
    }, []);

    // If the fetch/save APIs fail for any reason, convey a generic failure message to the user.
    useEffect(() => {
        if (rolesResponse.error || cohortTemplatesResponse.error || surveyFormResponse.error || saveSurveyFormResponse.error) {
            setError(true)
        }
    }, [rolesResponse, cohortTemplatesResponse, surveyFormResponse, saveSurveyFormResponse])

    if (!surveyFormResponse.requestComplete || !cohortTemplatesResponse.requestComplete || !rolesResponse.requestComplete)
        return <div>Loading...</div>

    // Sets either the Open Date+Open Time or Close date+Close Time from the Timing step.
    //
    // If there is no time selected, choosing the date defaults to 8:00 am time. If the time is already selected, it will remain unchanged.
    // If the date is unset, the time will also be unset.
    const setDate = (dateType, newDate) => {
        setFormData((oldFormData) => merge({},
            oldFormData,
            {
                timing: {
                    [`${dateType}Date`]: newDate,
                    [`${dateType}Time`]: newDate ? (oldFormData.timing[`${dateType}Time`] || "08:00") : null
                }
            }
        ))
    }

    // Sets the open time or close time from the Timing step
    const setTime = (dateType, newTime) => {
        setFormData((oldFormData) => merge({},
            oldFormData,
            {
                timing: {
                    [`${dateType}Time`]: newTime
                }
            }
        ))
    }

    const setProperties = (newPropertyFields) => setFormData(mergeWith({},
        formData,
        newPropertyFields,
        // In the case of an array, we want to replace the whole array not individual items
        (_, newValue) => isArray(newValue) ? newValue : undefined// eslint-disable-line no-undefined
    ))

    const getMomentFromString = (dateString) =>
        isEmptyString(dateString) ? null : moment(dateString, "YYYY-MM-DD")

    const scheduleSurvey = () => {
        setIsDialogOpen(false);
        onSave(cancel, true)();
    }

    // Pop up a confirm dialog before scheduling the survey
    const confirmScheduleDialog = () =>
        <ConfirmDialog
            isOpen={isDialogOpen}
            confirmText={"Yes, schedule"}
            onClose={() => setIsDialogOpen(false)}
            onConfirm={scheduleSurvey}
            headerText="Are you sure?"
            descriptionText={`Based on current data in your system, ${formData.title} will be available to students on ` +
                formattedDateTime(formData.timing, "open") +
                (formData.timing?.closeDate ? ` until ${formattedDateTime(formData.timing, "close")}.` : ".")} />

    // If there are any errors with the form, display a toast
    const getToastMessage = () => {
        if (!showToast) return null
        return <Toast message={"Unable to schedule until existing errors are addressed."} onTimeout={() => setShowToast(false)} />
    }

    const getContent = () => {
        return (
            <>
                {!!error && <ErrorMessage message={"There was an error processing your request. If this error continues to appear, please contact Starfish support."} />}
                <div className={css['outer-container']}>
                    <h1 className={css['content-header']}>{getWord('studentSurvey', false)}</h1>
                    <StudentSurveyTabs
                        onSelect={tabIndex => {selectTab(tabIndex)}}
                        selectedTab={selectedTab}
                        tabs={navigationTabs}>
                        <StudentSurveyPropertiesStep
                            roles={rolesResponse.data.roles}
                            surveyPermissions={formData.permissions}
                            title={formData.title}
                            surveyDescription={formData.description}
                            editable={!formData.validationPassed}
                            setProperties={setProperties} />
                        <StudentSurveyBuildStep
                            title={formData.title}
                            surveyElements={formData.elements}
                            editable={!formData.validationPassed}
                            setProperties={setProperties} />
                        <StudentSurveyAudienceStep
                            audience={formData.audience}
                            editable={!formData.validationPassed}
                            setProperties={setProperties} />
                        <StudentSurveyTimingStep
                            abbreviatedTZ={surveyFormResponse.data.abbreviatedTZ}
                            timezone={surveyFormResponse.data.timezone}
                            openDate={getMomentFromString(formData.timing.openDate)}
                            closeDate={getMomentFromString(formData.timing.closeDate)}
                            openTime={formData.timing.openTime}
                            closeTime={formData.timing.closeTime}
                            setOpenDate={(newOpenDate) => setDate("open", newOpenDate)}
                            setCloseDate={(newCloseDate) => setDate("close", newCloseDate)}
                            setOpenTime={(newOpenTime) => setTime("open", newOpenTime)}
                            setCloseTime={(newCloseTime) => setTime("close", newCloseTime)}
                            openDateClearable={!formData.validationPassed}
                            openDateTimeEditable={formData.validationPassed ? !formData.timingReached?.startDateTimeReached : true}
                            closeDateTimeEditable={formData.validationPassed ? !formData.timingReached?.endDateTimeReached : true} />
                        <StudentSurveyConfirmSetUp
                            cohortTemplates={cohortTemplatesResponse.data.cohortTemplates}
                            formData={formData}
                            goToTab={(tabClicked) => selectTab(navigationTabs.findIndex(tab => tab.name === tabClicked))} />
                    </StudentSurveyTabs>
                    {confirmScheduleDialog()}
                    {getToastMessage()}
                </div>
            </>
        )
   }

    const getFooter = () => (
        <Footer
            primaryButton={
                <div className={css['primary-buttons']}>
                    <StyledButton asLink onClick={onSave(cancel)}>Save & Exit</StyledButton>
                    <StyledButton onClick={lastTab() ? onScheduleClick : onSave(next)}>
                        {lastTab() ? "Schedule" : "Save & Next"}
                    </StyledButton>
                </div>}
            secondaryButton={<StyledButton asLink onClick={cancel}>Cancel</StyledButton>} />
    );

    return <StickyFooterLayout content={getContent()} footer={getFooter()} />
}

StudentSurveyTemplate.propTypes = {
    cancel: PropTypes.func.isRequired,
    isCopy: PropTypes.bool,
    surveyTemplateId: PropTypes.number,
    displayToast: PropTypes.func.isRequired
};

export default StudentSurveyTemplate;
